import { Component, OnInit } from '@angular/core';

import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first, map } from 'rxjs';
import { BaseWizardComponent } from '../base-wizard.component';
import { CategoryGroup, CategoryPurposeActivityDataModel, IActivityGroup, ICategoryPurposeActivityDataModel, IRegionalEcosystem, RegionalEcosystem, RegionalEcosystemDataModel } from '../models/veg-wizard-models';
import { RegionalEcosystemService } from '../services/regional-ecosystem.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';
import { PurposeEnum } from 'src/app/models/shared-models';

const reNotInList = 'My RE is not in the list'

@UntilDestroy()
@Component({
  selector: 'app-select-regional-ecosystem',
  templateUrl: './select-regional-ecosystem.component.html',
  styleUrls: ['./select-regional-ecosystem.component.css'],
  imports: [
    ReactiveFormsModule
],
})
export class SelectRegionalEcosystemComponent extends BaseWizardComponent implements OnInit {

  form: UntypedFormGroup;
  regionalEcosystemData: RegionalEcosystemDataModel;
  requiresActivity = false;
  activities: ICategoryPurposeActivityDataModel[];
  messages: string[] = [];
  showReSearch = false;
  searchFailed: [{ category: number, activityId: number, failed: boolean }] = [{ category: 0, activityId: 0, failed: false }]


  constructor(
    private formBuilder: UntypedFormBuilder,
    router: Router,
    private vwds: VegWizardDataService,
    private regionalEcosystemService: RegionalEcosystemService) {
    super();

    this.regionalEcosystemData = this.vwds.getRegionalEcosystems() ?? new RegionalEcosystemDataModel();
    this.requiresActivity = this.vwds.getCategories().some(category => category.requiresActivities);
    this.activities = this.vwds.getActivities()?.selectedActivities?.filter(activity => activity.areRegionalEcosystemsRequired) ?? [new CategoryPurposeActivityDataModel()];

    this.form = this.formBuilder.group({ regionalEcosystemCategoryArray: this.formBuilder.array([]) });
  }

  ngOnInit(): void {
    this.setRegionalEcosystemControls();
  }

  

  //For each category create a list of regional ecosystems with checkbox controls
  setRegionalEcosystemControls() {
    const activityIds = this.activities.map(i => i.activityId).filter(activityId => activityId != 0);
    this.regionalEcosystemService.getRegionalEcosystems$(activityIds).pipe(first(), untilDestroyed(this),
      map((result: CategoryGroup[]) => {
        result.forEach(category => {
          const reArray = this.formBuilder.array([]);
          const reManualArray = this.formBuilder.array([]);

          result.filter(regionalEcosystem => regionalEcosystem.categoryCode == category.categoryCode).forEach(re => {
            //Set available regional ecosystems checkbox controls
            re.activityGroups.forEach(activity => {
              activity.availableRegionalEcosystems?.forEach(re => {
                const checkBoxValue = this.regionalEcosystemData
                  ? this.regionalEcosystemData.regionalEcosystems.some(reData => reData.categoryPurposeId == re.categoryPurposeId && reData.regionalEcosystem == re.regionalEcosystem && this.compareActivities(reData.activityId, re.activityId))
                  : false;
                const reFormGroup = this.formBuilder.group({ regionalEcosystem: re, checked: new UntypedFormControl(checkBoxValue) } as IREFormGroupData);

                reArray.push(reFormGroup);
                reFormGroup.valueChanges.pipe(untilDestroyed(this))
                  .subscribe((reFormGroup: IREFormGroupData) => {
                    if (this.regionalEcosystemHasChanged(reFormGroup)) {
                      this.setRegionalEcosystemData(reFormGroup.regionalEcosystem, Boolean(reFormGroup.checked));
                    }
                  });
              })
            })
          });
          //Set manually added regional ecosystem checkbox controls
          this.regionalEcosystemData.regionalEcosystemsAddedFromSearchResults.filter(manualRe =>
            manualRe.categoryPurposeId == category.categoryPurposeId).forEach(re => {

              const manualReFormGroup = this.formBuilder.group({ regionalEcosystem: re, checked: new UntypedFormControl(true) } as IREFormGroupData);

              reManualArray.push(manualReFormGroup);
              manualReFormGroup.valueChanges.pipe(untilDestroyed(this))
                .subscribe((manualReForm: IREFormGroupData) => {
                  if (this.regionalEcosystemHasChanged(manualReForm)) {
                    this.setManuallyAddedRegionalEcosystemData(manualReForm.regionalEcosystem, Boolean(manualReForm.checked));
                  }
                });
            });
          //Add all checkbox controls to the master category array
          this.getRECategoryArray().push(this.formBuilder.group({
            category: `Category ${category.categoryCode}`,
            categoryPurposeId: category.categoryPurposeId,
            regionalEcosystems: reArray,
            manuallySelected: reManualArray
          }));
        });
      })).subscribe();
  }

  //Adds the regional ecosystem which the user searches for
  addManuallySelectedControl(activity: IActivityGroup) {
    activity.availableRegionalEcosystems?.forEach(re => this.regionalEcosystemData.regionalEcosystemsAddedFromSearchResults.push(re));

    if (!this.searchFailed.some(x => x.category == activity.categoryPurposeId && this.compareActivities(x.activityId, activity.activityId))) {
      this.searchFailed.push({ category: activity.categoryPurposeId, activityId: activity.activityId ?? 0, failed: false });
    }

    const manuallySelected = this.getManuallySelectedArray(this.getRECategoryArray().controls.filter(control => control.value.categoryPurposeId == activity.categoryPurposeId)[0]);

    activity.availableRegionalEcosystems?.forEach(re => {
      const reFormGroup = this.formBuilder.group({ regionalEcosystem: re, checked: new UntypedFormControl(true) } as IREFormGroupData);

      manuallySelected.push(reFormGroup);
      reFormGroup.valueChanges.pipe(untilDestroyed(this))
        .subscribe((manualReForm: IREFormGroupData) => {
          if (this.regionalEcosystemHasChanged(manualReForm)) {
            this.setManuallyAddedRegionalEcosystemData(manualReForm.regionalEcosystem, Boolean(manualReForm.checked));
          }
        });
    });
  }

  //Returns true if the state of the data has changed
  regionalEcosystemHasChanged(reToAdd: IREFormGroupData): boolean {
    return reToAdd.checked
      ? !this.regionalEcosystemData.regionalEcosystems
        .some(re => re.regionalEcosystem == reToAdd.regionalEcosystem.regionalEcosystem && re.categoryPurposeId == reToAdd.regionalEcosystem.categoryPurposeId && this.compareActivities(re.activityId, reToAdd.regionalEcosystem.activityId))
      : this.regionalEcosystemData.regionalEcosystems
        .some(re => re.regionalEcosystem == reToAdd.regionalEcosystem.regionalEcosystem && re.categoryPurposeId == reToAdd.regionalEcosystem.categoryPurposeId && this.compareActivities(re.activityId, reToAdd.regionalEcosystem.activityId));
  }

  //Unchecks all manually added checkboxes for the given category purpose
  setAllManuallySelected(categoryPurposeId: number, activityId: number, checked: boolean) {
    const manuallySelected = this.getManuallySelectedArray(this.getRECategoryArray().controls.filter(control => control.value.categoryPurposeId == categoryPurposeId)[0]);
    const valuesToUncheck = manuallySelected.value.filter((re: IREFormGroupData) => this.compareActivities(re.regionalEcosystem.activityId, activityId));
    valuesToUncheck.forEach((x: any) => {
      x.checked = checked;
    });

    manuallySelected.patchValue(valuesToUncheck);
  }

  //Add or remove the selected regional ecosystem to the regional ecosystem data model
  setRegionalEcosystemData(selectedRe: IRegionalEcosystem, isChecked: boolean) {
    if (isChecked && !this.regionalEcosystemData.regionalEcosystems
      .some(x => x.regionalEcosystem == selectedRe.regionalEcosystem && x.categoryPurposeId == selectedRe.categoryPurposeId && this.compareActivities(x.activityId, selectedRe.activityId))) {
      this.regionalEcosystemData.regionalEcosystems.push(selectedRe)
    }
    else if (!isChecked && this.regionalEcosystemData.regionalEcosystems
      .some(x => x.regionalEcosystem == selectedRe.regionalEcosystem && x.categoryPurposeId == selectedRe.categoryPurposeId && this.compareActivities(x.activityId, selectedRe.activityId))) {
      this.regionalEcosystemData.regionalEcosystems.splice(this.regionalEcosystemData.regionalEcosystems
        .findIndex(x => x.categoryPurposeId == selectedRe.categoryPurposeId && x.regionalEcosystem == selectedRe.regionalEcosystem && this.compareActivities(x.activityId, selectedRe.activityId)), 1);
    }
    else {
      //Nothing has changed so stop processing now
      return;
    }

    this.setDistinctMessagesList();
    this.showReSearch = this.regionalEcosystemData.regionalEcosystems.some(re => re.regionalEcosystem == reNotInList);

    if (selectedRe.regionalEcosystem == reNotInList) {
      this.setAllManuallySelected(selectedRe.categoryPurposeId, selectedRe.activityId ?? 0, isChecked);
    }
  }

  //Add or remove the selected regional ecosystem to the manually added data model
  setManuallyAddedRegionalEcosystemData(selectedRe: IRegionalEcosystem, isChecked: boolean) {
    if (isChecked && !this.regionalEcosystemData.regionalEcosystemsAddedFromSearchResults
      .some(x => x.regionalEcosystem == selectedRe.regionalEcosystem && x.categoryPurposeId == selectedRe.categoryPurposeId)) {
      this.regionalEcosystemData.regionalEcosystemsAddedFromSearchResults.push(selectedRe)
    }
    else if (!isChecked && this.regionalEcosystemData.regionalEcosystemsAddedFromSearchResults
      .some(x => x.regionalEcosystem == selectedRe.regionalEcosystem && x.categoryPurposeId == selectedRe.categoryPurposeId)) {
      this.regionalEcosystemData.regionalEcosystemsAddedFromSearchResults?.splice(this.regionalEcosystemData.regionalEcosystemsAddedFromSearchResults
        .findIndex(x => x.categoryPurposeId == selectedRe.categoryPurposeId && x.regionalEcosystem == selectedRe.regionalEcosystem), 1);
    }
    this.setRegionalEcosystemData(selectedRe, isChecked);

    //Trigger the validation on the master RE list to ensure we haven't reached the limit
    this.getRegionalEcosystemArray(this.getRECategoryArrayByCategory(selectedRe.categoryPurposeId)).controls[0].updateValueAndValidity();
  }

  //Replace the current list of messages with a distinct list of messages from the currently selected regional ecosystems
  setDistinctMessagesList() {
    const messages: string[] = [];
    this.regionalEcosystemData.regionalEcosystems.forEach(reGroup => {

      if (reGroup.message) {
        messages.push(reGroup.message);
      }
    });
    this.messages = [...new Set(messages)];
  }

  //Validates that at least one regional ecosystem has been manually added if "my RE is not in the list" has been selected
  atLeastOneManualReSelectedValidator(categoryPurposeId: number, activityId: number): ValidatorFn {
    let reSelectedForEachCategory = true;

    this.getRECategoryArray().controls.forEach(reCategoryControl => {
      this.getRegionalEcosystemArray(reCategoryControl).controls
        .filter(control => control.value.regionalEcosystem.categoryPurposeId == categoryPurposeId && this.compareActivities(control.value.regionalEcosystem.activityId, activityId))
        .forEach(reArrayControl => {
          if (reArrayControl.value.regionalEcosystem.regionalEcosystem == reNotInList && reArrayControl.value.checked) {
            const manualySelectedControls = this.getManuallySelectedArray(reCategoryControl).controls.filter((re: AbstractControl) => this.compareActivities(re.value.regionalEcosystem.activityId, activityId));
            if (manualySelectedControls.length == 0) {
              reSelectedForEachCategory = false;
            }
            else if (!this.getManuallySelectedArray(reCategoryControl)?.controls.some(reControl => {
              return reControl.value.checked;
            })) {
              reSelectedForEachCategory = false;
            }
          }
        });
    });
    return () => {
      return reSelectedForEachCategory ? null : { [`manualReRequiredValidationError${categoryPurposeId}${activityId}`]: true };
    };
  }

  //Validates that at least one regional ecosystem has been selected for each category and activity
  atLeastOneReSelectedForEachCategoryValidator(): ValidatorFn {
    let reSelectedForEachCategory = true;
    this.getRECategoryArray().controls.forEach(reArrayControl => {
      this.activities.forEach(activity => {
        if (!this.getRegionalEcosystemArray(reArrayControl).controls.some(reControl => {
          //If the categorPurposeIds don't match return true, as we only want to check activities for their respective categories 
          return this.requiresActivity && reControl.value.regionalEcosystem.categoryPurposeId != activity.categoryPurposeId ||
            (this.compareActivities(reControl.value.regionalEcosystem.activityId, activity.activityId) && reControl.value.checked);
        })) {
          reSelectedForEachCategory = false;
        }
      })
    });
    return () => {
      return reSelectedForEachCategory ? null : { reRequiredValidationError: true };
    };
  }

  regionalEcosystemsLimitValidator(): ValidatorFn {
    let limitReached = false;
    const reSelectedLimit = 10;
    let categoryPurposeId = 0;

    this.getRECategoryArray().controls.forEach(reArrayControl => {
      const selectedRes = this.getRegionalEcosystemArray(reArrayControl).controls.filter(reControl => reControl.value.checked).length
        + this.getManuallySelectedArray(reArrayControl).controls.filter(reControl => reControl.value.checked).length;
      if (selectedRes > reSelectedLimit) {
        categoryPurposeId = reArrayControl.value.categoryPurposeId;
        limitReached = true;
      }
    });
    return () => {
      return limitReached ? { [`reSelectedLimitValidationError${categoryPurposeId}`]: true } : null;
    }
  }

  showReSearchForCategory(categoryPurposeId: number, activityId: number): boolean {
    return this.regionalEcosystemData.regionalEcosystems
      .some(x => x.categoryPurposeId == categoryPurposeId && this.compareActivities(x.activityId, activityId) && x.regionalEcosystem == reNotInList);
  }

  showManuallyAddedList(categoryPurposeId: number, activityId: number) {
    return this.getManuallySelectedArray(this.getRECategoryArray().controls
      .filter(control => control.value.categoryPurposeId == categoryPurposeId
      )[0]).value.filter((reFormGroup: IREFormGroupData) => this.compareActivities(reFormGroup.regionalEcosystem.activityId, activityId)).length > 0;
  }

  validateManualSelection(categoryPurposeId: number) {
    const manuallySelected = this.getManuallySelectedArray(this.getRECategoryArray().controls
      .filter(control => control.value.categoryPurposeId == categoryPurposeId)[0]);
    manuallySelected.clearValidators();
    manuallySelected.updateValueAndValidity();
    this.activities.forEach(activity =>
      manuallySelected.addValidators([this.atLeastOneManualReSelectedValidator(categoryPurposeId, activity.activityId)]));

    manuallySelected.updateValueAndValidity();
  }

  //Adds the regional ecosystem which the user has searched for
  addRegionalEcosystem(reText: string, re: RegionalEcosystem) {
    if (reText) {
      this.searchFailed.filter(x => x.category == re.categoryPurposeId && this.compareActivities(x.activityId, re.activityId)).length > 0 ?
        this.searchFailed.filter(x => x.category == re.categoryPurposeId && this.compareActivities(x.activityId, re.activityId))[0].failed = false :
        this.searchFailed.push({ category: re.categoryPurposeId, activityId: re.activityId, failed: false });

      this.regionalEcosystemService.searchRegionEcosystem$(reText).subscribe((searchREResults: CategoryGroup[]) => {
        if (searchREResults.length > 0 && searchREResults.some(category => category.activityGroups.some(activity => activity.activityId == re.activityId))) {

          let matchingCategories = searchREResults.filter(category => category.categoryPurposeId == re.categoryPurposeId);
          let activityGroups = matchingCategories[0].activityGroups.filter(activity => this.compareActivities(activity.activityId, re.activityId));

          activityGroups.forEach(activityGroup => {
            this.addManuallySelectedControl(activityGroup)
            activityGroup.availableRegionalEcosystems?.forEach(activityGroup => {
              this.setManuallyAddedRegionalEcosystemData(activityGroup, true);
            });
          });
        }
        else {
          this.searchFailed.filter(x => x.category == re.categoryPurposeId && this.compareActivities(x.activityId, re.activityId))[0].failed = true;
        }
      });
    }
    else {
      this.searchFailed.filter(x => x.category == re.categoryPurposeId && this.compareActivities(x.activityId, re.activityId))[0].failed = true;
    }
  }

  getRECategoryArrayByCategory(categoryPurposeId: number) {
    return this.getRECategoryArray().controls.filter(x => x.value.categoryPurposeId == categoryPurposeId)[0]
  }

  getRECategoryArray(): UntypedFormArray {
    return this.form.get('regionalEcosystemCategoryArray') as UntypedFormArray;
  }

  getManuallySelectedArray(control: AbstractControl): UntypedFormArray {
    return control.get('manuallySelected') as UntypedFormArray;
  }

  getRegionalEcosystemArray(control: AbstractControl): UntypedFormArray {
    return control.get('regionalEcosystems') as UntypedFormArray;
  }

  //Compares activities provided if the current categories require activities
  compareActivities(activityIdOne?: number | null, activityIdTwo?: number | null): boolean {
    return !this.requiresActivity || (this.requiresActivity && activityIdOne == activityIdTwo);
  }

  override next(): void {
    this.form.setValidators([this.atLeastOneReSelectedForEachCategoryValidator(), this.regionalEcosystemsLimitValidator()]);
    this.getRECategoryArray().value.forEach((categoryControl: IRECategoryControl) => {
      this.validateManualSelection(categoryControl.categoryPurposeId);
    });
    this.form.markAsDirty();
    this.form.updateValueAndValidity();

    if (!this.form.valid) return;

    this.vwds.setRegionalEcosystems$(this.regionalEcosystemData)
      .pipe(
        first(),
        untilDestroyed(this)
      )
      .subscribe(() => {
        super.next();
      });
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }
}

export interface IREFormGroupData {
  regionalEcosystem: IRegionalEcosystem,
  checked: UntypedFormControl,
}

export interface IRECategoryControl {
  category: string,
  categoryPurposeId: number,
  manuallySelected: IREFormGroupData[],
  regionalEcosystems: IREFormGroupData[]
}